import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { Spinner } from "@/components/ui/Spinner";

interface GuestRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Route wrapper that only allows unauthenticated users.
 * Redirects authenticated users to account page or specified destination.
 * @param props - Component props
 * @param props.children - Child components to render when not authenticated
 * @param props.redirectTo - Where to redirect authenticated users (default: /account)
 * @returns The guest content or redirect to account
 */
export function GuestRoute({ children, redirectTo = "/account" }: GuestRouteProps) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // If user came from a protected route, redirect them back there
    const stateObj = location.state as { from?: Location } | null;
    const from = stateObj?.from?.pathname;
    const destination = from ?? redirectTo;
    return <Navigate to={destination} replace />;
  }

  return <>{children}</>;
}
